import React, { FormEvent, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { postRequest } from "../../../../utils/httpRequest";
import { trackEnquiryConversion } from "../../../../utils/analytics";

interface EnquiryForm {
  name: string;
  company: string;
  email: string;
  teamSize: number;
  statement: string;
}

const EnquiryForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [enquiryForm, setEnquiryForm] = useState<EnquiryForm>({
    name: "",
    company: "",
    email: "",
    teamSize: 10,
    statement: "",
  });

  const updateEnquiryField = <K extends keyof EnquiryForm>(
    field: K,
    value: EnquiryForm[K],
  ) => {
    setEnquiryForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setEnquiryForm({
      name: "",
      company: "",
      email: "",
      teamSize: 10,
      statement: "",
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setSubmitted(false);
    setErrorMessage("");
    setCaptchaError("");

    const recaptchaValue = recaptchaRef.current?.getValue();

    const workshopEnquiryBody = {
      workshop_enquiry: {
        name: enquiryForm.name,
        company: enquiryForm.company,
        email: enquiryForm.email,
        team_size: enquiryForm.teamSize,
        statement: enquiryForm.statement,
      },
      recaptcha_token: recaptchaValue,
    };

    return postRequest("/api/workshop_enquiries.json", workshopEnquiryBody)
      .then(() => {
        setSaving(false);
        setSubmitted(true);
        resetForm();
        recaptchaRef.current?.reset();
        trackEnquiryConversion({
          event: "workshop_enquiry",
          formLocation: "workshops_enquiry",
          teamSize: enquiryForm.teamSize,
        });
      })
      .catch((error: any) => {
        setSaving(false);
        recaptchaRef.current?.reset();

        if (error?.response?.data?.captcha_error) {
          setCaptchaError(
            "Please complete the reCAPTCHA challenge and try again.",
          );
        } else {
          setErrorMessage(
            "Sorry, something went wrong. Please try again or email groundcontrol@42courses.com.",
          );
        }
      });
  };

  return (
    <section
      className="form-section"
      id="enquire"
    >
      <div className="hero-pattern" aria-hidden="true">
        <svg
          className="bg"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          <svg x="8%" y="20%" overflow="visible">
            <use
              href="#blob-2"
              fill="#F3EC7A"
              transform="scale(1.6)"
              opacity="0.20"
            />
          </svg>
          <svg x="88%" y="74%" overflow="visible">
            <use
              href="#blob-1"
              fill="#FFA5BC"
              transform="scale(1.8)"
              opacity="0.15"
            />
          </svg>
        </svg>
      </div>

      <div className="container">
        <div className="form-head">
          <h2>
            Tell us what you're <em>after</em>.
          </h2>
          <p>
            We'll come back within one working day with a tailored proposal.
          </p>
        </div>

        <form
          className="enquiry"
          onSubmit={handleSubmit}
        >
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="name">Your name</label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                aria-required="true"
                value={enquiryForm.name}
                onChange={(event) =>
                  updateEnquiryField("name", event.target.value)
                }
              />
            </div>
            <div className="form-field">
              <label htmlFor="company">Company</label>
              <input
                id="company"
                name="company"
                type="text"
                autoComplete="organization"
                required
                aria-required="true"
                value={enquiryForm.company}
                onChange={(event) =>
                  updateEnquiryField("company", event.target.value)
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                aria-required="true"
                value={enquiryForm.email}
                onChange={(event) =>
                  updateEnquiryField("email", event.target.value)
                }
              />
            </div>
            <div className="form-field">
              <label htmlFor="size">Team size</label>
              <select
                id="size"
                name="size"
                autoComplete="off"
                value={enquiryForm.teamSize}
                onChange={(event) =>
                  updateEnquiryField("teamSize", Number(event.target.value))
                }
              >
                <option value={10}>Under 10</option>
                <option value={30}>10-30</option>
                <option value={100}>30-100</option>
                <option value={101}>100+</option>
              </select>
            </div>
          </div>

          <div className="form-field">
            {/* Marked optional because it genuinely is — the model only
                requires name, company, email and team size. A big empty box
                with no such reassurance reads as homework, and homework is
                where enquiries get abandoned. */}
            <label htmlFor="interest">
              Which workshop, or what are you trying to solve?{" "}
              <span className="field-optional">(Optional)</span>
            </label>
            <textarea
              id="interest"
              name="interest"
              placeholder="A sentence is plenty."
              value={enquiryForm.statement}
              onChange={(event) =>
                updateEnquiryField("statement", event.target.value)
              }
            />
          </div>

          <div className="form-recaptcha">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.RECAPTCHA_SITE_TOKEN}
            />
            {captchaError && (
              <p className="form-captcha-error" role="alert">
                {captchaError}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-blue submit-btn"
            disabled={saving}
          >
            {saving ? "Sending..." : "Send enquiry"}
          </button>
          {submitted && (
            <p
              id="form-status"
              role="status"
              aria-live="polite"
              className="form-status"
            >
              Thanks. We will be in touch within one working day.
            </p>
          )}
          {errorMessage && (
            <p
              id="form-error"
              role="alert"
              className="form-status"
            >
              {errorMessage}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default EnquiryForm;
